import { useEffect, useState, useMemo } from "react";
import { collection, getDocs, getFirestore, doc, updateDoc } from "firebase/firestore";
import { firebaseApp } from "../../firebase";
import { FunnelIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import * as XLSX from "xlsx";
import { useAuth } from "../../context/AuthContext";
import { getMembershipFromUsers } from "../../services/firestore/userService";
import { encodeFirestoreId } from "../../services/firestore/firebaseService";

const COLORS = [
  { label: "None", value: "" },
  { label: "Red", value: "red" },
  { label: "Blue", value: "blue" },
  { label: "Green", value: "green" },
  { label: "Yellow", value: "yellow" },
  { label: "Purple", value: "purple" },
];

const FormResponseModalV2 = ({ open, setOpen, formId, editingForm }) => {
  const db = getFirestore(firebaseApp);
  const [responses, setResponses] = useState([]);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [filter, setFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [attendedFilter, setAttendedFilter] = useState("");
  const [memberships, setMemberships] = useState([]); // 🆕
  const [membersMap, setMembersMap] = useState({}); // 🆕 quick lookup
  const { userData } = useAuth();

  // 🆕 Fetch membership data
  useEffect(() => {
    const fetchMemberships = async () => {
      if (!userData?.uid) return;
      try {
        const result = await getMembershipFromUsers(userData.uid);
        // sort memberships by createdAt desc and map latest per sgloop
        const sorted = result.sort((a, b) => b.createdAt.seconds - a.createdAt.seconds);
        const map = {};
        for (const m of sorted) {
          const ref = m.subSGloopReference?.id;
          if (ref && !map[ref]) map[ref] = m; // first occurrence = latest
        }
        console.log(sorted)
        setMemberships(sorted);
        setMembersMap(map);
      } catch (err) {
        console.error("Error fetching memberships:", err);
      }
    };
    if (open) fetchMemberships();
  }, [open, userData]);

  // 🆕 Helper: check if response’s sgloopReference is a member
  const getMembershipForResponse = (res) => {
    console.log(res)
    if (res.userUID != null) {
      const subRef = res.sgloopReference?.id || editingForm?.sgloopGroup?.id;
      if (!subRef) return null;
      console.log(membersMap[subRef])
      return membersMap[subRef] || null;
    }
    return null;
  };


  useEffect(() => {
    if (!open || !formId) return;

    const fetchResponses = async () => {
      try {
        setLoading(true);
        const responsesCol = collection(db, "forms", formId, "responses");
        const snap = await getDocs(responsesCol);
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        data.sort((a, b) => b.submittedAt.seconds - a.submittedAt.seconds);
        setResponses(data);
      } catch (err) {
        console.error("Error fetching responses:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchResponses();
  }, [open, formId]);

  // 🧠 When rendering list view:
  // Instead of previewAnswer, determine the display name
  const getDisplayName = (res) => {
    const membership = getMembershipForResponse(res);
    console.log(membership)
    if (membership) {
      // show membership name (support Name / name)
      return membership.Name || membership.name || "Member";
    }
    // fallback to answer object
    const name = res.answers?.Name || res.answers?.name;
    return name || "-";
  };

  const formatDate = (ts) => {
    const date = new Date(ts.seconds * 1000);
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const groupByMonth = (list) =>
    list.reduce((acc, res) => {
      const d = new Date(res.submittedAt.seconds * 1000);
      const monthYear = d.toLocaleString("default", { month: "long", year: "numeric" });
      if (!acc[monthYear]) acc[monthYear] = [];
      acc[monthYear].push(res);
      return acc;
    }, {});

  const updateColor = async (resId, color) => {
    try {
      const ref = doc(db, "forms", formId, "responses", resId);
      await updateDoc(ref, { color });
      setResponses((prev) => prev.map((r) => (r.id === resId ? { ...r, color } : r)));
      if (selectedResponse?.id === resId) {
        setSelectedResponse({ ...selectedResponse, color });
      }
    } catch (err) {
      console.error("Error updating color:", err);
    }
  };

  const toggleAttended = async (resId, current) => {
    try {
      const ref = doc(db, "forms", formId, "responses", resId);
      await updateDoc(ref, { attended: !current });
      setResponses((prev) =>
        prev.map((r) => (r.id === resId ? { ...r, attended: !current } : r))
      );
      if (selectedResponse?.id === resId) {
        setSelectedResponse({ ...selectedResponse, attended: !current });
      }
    } catch (err) {
      console.error("Error updating attended:", err);
    }
  };

  const exportToExcel = () => {
  if (responses.length === 0) return;

  // 🔹 Helper: flatten membership fields including repeatable groups
  const flattenMembership = (membership) => {
    const flat = {};

    Object.entries(membership).forEach(([key, val]) => {
      if (Array.isArray(val) && val.length > 0 && typeof val[0] === "object") {
        // repeatable group like Dogs
        val.forEach((obj, idx) => {
          Object.entries(obj).forEach(([subKey, subVal]) => {
            flat[`Membership - ${key} ${idx + 1} - ${subKey}`] = subVal;
          });
        });
      } else if (typeof val !== "object") {
        flat[`Membership - ${key}`] = val;
      }
    });

    return flat;
  };

  // 🔹 Collect all unique headers
  const allQuestions = new Set();

  responses.forEach((res) => {
    const answers = res.answers || {};

    // Flatten answers
    Object.entries(answers).forEach(([key, val]) => {
      if (Array.isArray(val) && val.length > 0 && typeof val[0] === "object") {
        val.forEach((obj, idx) => {
          Object.keys(obj).forEach((subKey) => {
            allQuestions.add(`${key} ${idx + 1} - ${subKey}`);
          });
        });
      } else {
        allQuestions.add(key);
      }
    });

    // Flatten membership
    const membership = getMembershipForResponse(res);
    if (membership) {
      const flatMem = flattenMembership(membership);
      Object.keys(flatMem).forEach((k) => allQuestions.add(k));
    }
  });

  const headers = [...allQuestions, "Attended", "Color", "Submitted At"];

  // 🔹 Build data rows
  const data = responses.map((res) => {
    const row = {};
    const answers = res.answers || {};
    const membership = getMembershipForResponse(res);
    const flatMem = membership ? flattenMembership(membership) : {};

    headers.forEach((header) => {
      let value = "-";

      // Membership fields
      if (flatMem[header]) {
        value = flatMem[header];
      } else if (answers) {
        // Regular answers
        Object.entries(answers).forEach(([key, val]) => {
          if (Array.isArray(val) && val.length > 0 && typeof val[0] === "object") {
            val.forEach((obj, idx) => {
              Object.entries(obj).forEach(([subKey, subVal]) => {
                const dynamicKey = `${key} ${idx + 1} - ${subKey}`;
                if (header === dynamicKey) value = subVal;
              });
            });
          } else if (header === key) {
            value = val;
          }
        });
      }

      // Standard fields
      if (header === "Attended") value = res.attended ? "Yes" : "No";
      if (header === "Color") value = res.color || "-";
      if (header === "Submitted At") value = formatDate(res.submittedAt);

      row[header] = value;
    });

    return row;
  });

  // 🔹 Generate Excel
  const ws = XLSX.utils.json_to_sheet(data, { header: headers });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Responses");

  XLSX.writeFile(wb, `form-${formId}-responses.xlsx`);
};




  const filteredResponses = useMemo(() => {
    let res = [...responses];
    if (filter) res = res.filter((r) => r.color === filter);
    if (attendedFilter === "yes") res = res.filter((r) => r.attended);
    else if (attendedFilter === "no") res = res.filter((r) => !r.attended);
    if (searchText.trim()) {
      const lowerText = searchText.toLowerCase();
      res = res.filter((r) =>
        Object.entries(r.answers || {}).some(([q, ans]) =>
          `${q} ${ans}`.toLowerCase().includes(lowerText)
        )
      );
    }
    return res;
  }, [responses, filter, attendedFilter, searchText]);

  if (!open) return null;

  const currentIndex = selectedResponse
    ? filteredResponses.findIndex((r) => r.id === selectedResponse.id)
    : -1;

  const goPrev = () => currentIndex > 0 && setSelectedResponse(filteredResponses[currentIndex - 1]);
  const goNext = () =>
    currentIndex < filteredResponses.length - 1 &&
    setSelectedResponse(filteredResponses[currentIndex + 1]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <div className="bg-white w-full max-w-3xl mx-2 sm:mx-4 p-4 sm:p-6 rounded shadow-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          {!selectedResponse ? (
            <h2 className="text-lg sm:text-2xl font-bold">Form Responses</h2>
          ) : (
            <button
              onClick={() => setSelectedResponse(null)}
              className="text-blue-500 text-sm sm:text-base"
            >
              ← Back
            </button>
          )}
          <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-gray-700 text-base sm:text-lg">
            ✕
          </button>
        </div>

        {/* Stats */}
        <div className="text-sm text-gray-600 mb-2">
          {responses.length > 0 &&
            `${responses.filter((r) => r.attended).length} out of ${responses.length} attended`}
        </div>

        {/* LIST VIEW */}
        {!selectedResponse && (
          <div>
            {/* Filters */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <FunnelIcon className="h-5 w-5 text-gray-500" />
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="border rounded p-1 text-sm"
                >
                  <option value="">All Colors</option>
                  {COLORS.filter((c) => c.value).map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <select
                  value={attendedFilter}
                  onChange={(e) => setAttendedFilter(e.target.value)}
                  className="border rounded p-1 text-sm"
                >
                  <option value="">All Attendance</option>
                  <option value="yes">Attended</option>
                  <option value="no">Not Attended</option>
                </select>
              </div>
              <button
                onClick={exportToExcel}
                className="bg-green-700 hover:bg-green-600 text-white text-sm sm:text-base px-3 py-1 rounded"
              >
                Export Data
              </button>
            </div>

            {/* Search */}
            <div className="flex items-center gap-2 mb-4">
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search responses..."
                className="flex-1 border rounded p-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {loading && <ArrowPathIcon className="w-5 h-5 text-gray-500 animate-spin" />}
            </div>

            {/* Results */}
            {filteredResponses.length === 0 ? (
              <p className="text-sm text-gray-500">No matching responses.</p>
            ) : (
              Object.entries(groupByMonth(filteredResponses)).map(([month, subs]) => (
                <div key={month} className="mb-4">
                  <h3 className="text-xs font-semibold text-gray-500 mb-2">
                    {month.toUpperCase()}
                  </h3>
                  <div className="border rounded divide-y">
                    {subs.map((res) => (
                      <div
                        key={res.id}
                        className="p-3 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setSelectedResponse(res)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            {formatDate(res.submittedAt)}
                            {res.color && (
                              <span
                                className={`ml-2 inline-block w-3 h-3 rounded-full bg-${res.color}-500`}
                              ></span>
                            )}
                          </div>
                          <select
                            value={res.color || ""}
                            onChange={(e) => updateColor(res.id, e.target.value)}
                            className="border rounded p-1 text-xs"
                          >
                            {COLORS.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <p className="text-sm text-gray-600 mt-1 truncate">
                          {Object.values(res.answers || {})[0] || "-"}
                        </p>
                        {/* 🆕 Show name */}
                        <p className="text-sm text-gray-800 mt-1 font-medium">{getDisplayName(res)}</p>
                        <span
                          className={`mt-1 inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${res.attended
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                            }`}
                        >
                          {res.attended ? "Attended" : "Not Attended"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* DETAIL VIEW */}
        {selectedResponse && (
          <div>
            {/* 🧩 Membership section */}
            {(() => {
              const membership = getMembershipForResponse(selectedResponse);
              if (membership) {
                const cleanMembership = Object.fromEntries(
                  Object.entries(membership).filter(([key, val]) => typeof val !== "object")
                );
                return (
                  <div className="mb-6 p-3 border rounded bg-blue-50">
                    <h3 className="font-semibold text-blue-700 mb-2">Membership Details</h3>
                    <div className="space-y-4 text-sm">
                      {Object.entries(membership).map(([key, val]) => {
                        // 🔹 Handle normal text fields
                        console.log(key)
                        if (typeof val !== "object" || val === null) {
                          return (
                            <div key={key} className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                              <span className="font-medium capitalize">{key}:</span>
                              <span>{key === "id" ? encodeFirestoreId(val) : String(val)}</span>
                            </div>
                          );
                        }

                        // 🔹 Handle arrays of objects (like Dogs, Houses, etc.)
                        if (Array.isArray(val) && val.length > 0 && typeof val[0] === "object") {
                          return (
                            <div key={key} className="p-3 border rounded bg-white/60">
                              <h4 className="font-semibold text-blue-700 mb-2 capitalize">{key}</h4>
                              {val.map((item, idx) => (
                                <div
                                  key={idx}
                                  className="mb-3 p-2 border rounded bg-gray-50 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1"
                                >
                                  {Object.entries(item).map(([subKey, subVal]) => (
                                    <div key={subKey}>
                                      <span className="font-medium capitalize">{subKey}:</span>{" "}
                                      <span>{String(subVal)}</span>
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          );
                        }

                        // 🔹 Handle other object types (like nested Firestore references)
                        return null;
                      })}
                    </div>

                  </div>
                );
              }
              return null;
            })()}

            {/* The rest of your detail view (answers, attendance, navigation) */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">{formatDate(selectedResponse.submittedAt)}</h2>
              <select
                value={selectedResponse.color || ""}
                onChange={(e) => updateColor(selectedResponse.id, e.target.value)}
                className="border rounded p-1 text-sm"
              >
                {COLORS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-3 mb-4">
              {Object.entries(selectedResponse.answers || {}).map(([question, answer]) => (
                <div key={question}>
                  <p className="text-sm text-gray-600">{question}</p>
                  <p className="font-medium ml-2">{answer || "-"}</p>
                </div>
              ))}
            </div>

            <label className="flex items-center gap-2 text-sm sm:text-base mb-6">
              <input
                type="checkbox"
                checked={selectedResponse.attended || false}
                onChange={() => toggleAttended(selectedResponse.id, selectedResponse.attended)}
                className="w-4 h-4"
              />
              Attended
            </label>

            <div className="flex justify-between">
              <button
                onClick={goPrev}
                disabled={currentIndex === 0}
                className={`px-4 py-2 rounded ${currentIndex === 0
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                  }`}
              >
                Prev
              </button>
              <button
                onClick={goNext}
                disabled={currentIndex === filteredResponses.length - 1}
                className={`px-4 py-2 rounded ${currentIndex === filteredResponses.length - 1
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                  }`}
              >
                Next
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default FormResponseModalV2;
